/*
 * License area (audit Phase 3.3). Replaces wwwroot/js/License/List.js and
 * wwwroot/js/License/Detail.js on the Phase 3.1 core. No jQuery.
 *
 * The binder editor and the quota editor are big enough to own their files --
 * areas/license-binder.ts and areas/license-quota.ts -- but all three are one
 * screen: the License Detail modal, whose tabs they fill. Anything shared
 * between them (the modal's id, the busy indicator, which tab to reload) lives
 * here and they import it, so there is one answer to "which license is open"
 * rather than three readings of the same hidden input.
 *
 * Lazy tabs are core/tabs' job now. The legacy file bound two document-level
 * jQuery handlers of its own -- one on shown.bs.tab and one on
 * shown.bs.modal -- to load the active tab when the modal opened; the strip
 * carries data-mcp-autotab instead and core/tabs does both.
 */
import { ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary, url as absoluteUrl } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { loadTab, markLoaded } from '../core/tabs';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

export const MODAL = 'LicenseModal';
const CREATE_FORM = 'LicenseCreateForm';
const DETAIL_ALERT = 'LicenseDetailMessageContainer';

/** The open license. Every verb on this screen needs it and the modal holds exactly one. */
export function licenseId(): string {
    return (one('#LicenseId') as HTMLInputElement | null)?.value ?? '';
}

/** The domain the console is pointed at; the list and the create form are both scoped to it. */
export function currentDomainId(): string {
    return (one('#CurrentDomainId') as HTMLInputElement | null)?.value ?? '0';
}

export function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/** Report a failed write as a toast and tell ajaxJson not to also render an alert. */
export function failToast(title: string): (data: unknown) => boolean {
    return data => {
        toast('danger', title, summaryOf(data));
        return true;
    };
}

function busy(working: boolean): void {
    for (const node of all('#LicenseBusyIndicator')) node.classList.toggle('d-none', !working);
}

/**
 * Re-fetch one tab of the open modal without re-rendering the rest of it.
 *
 * The counts in the tab headers and the rows underneath both come from the
 * tab's own endpoint, so a write anywhere in a tab reloads that tab and leaves
 * the operator's place in the others alone. `data-loaded` is cleared first
 * because core/tabs' loader treats a loaded tab as done.
 */
export function reloadTab(paneSelector: string): Promise<void> {
    const tab = one(`.license-modal [data-bs-target="${paneSelector}"]`);
    if (!tab) return Promise.resolve();
    markLoaded(tab, false);
    return loadTab(tab);
}

export const List = {
    ShowDetail(id: string): void {
        void ajaxModal({
            modal: MODAL,
            url: `License/Detail/${id}`,
            title: null,
            errorTitle: `License ${id}`,
            errorMessage: 'License Detail failed to load:',
        });
    },

    NewLicense(): void {
        void ajaxModal({
            modal: MODAL,
            url: `License/Create/${currentDomainId()}`,
            formId: CREATE_FORM,
            focusId: 'Name',
            errorTitle: 'New License',
            errorMessage: 'Failed to render new license form',
        });
    },

    NewLicenseSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        // The legacy version posted whatever was in the form and let the Api
        // answer -- so a blank Name, the one field the form marks required,
        // travelled to the server to be refused there. The template create
        // form has always gated on its own rules; this one now does too.
        if (!form || !isValid(form)) return;
        const working = (on: boolean): void => {
            for (const node of all(`#${CREATE_FORM} .fa-save`)) node.classList.toggle('d-none', on);
            for (const node of all(`#${CREATE_FORM} .fa-spinner`)) node.classList.toggle('d-none', !on);
        };
        working(true);
        void ajaxJson({
            url: 'License',
            method: 'POST',
            data: serialize(form),
            alertInto: 'LicenseCreateMessageContainer',
            errorTitle: 'Failed to create license',
            onSuccess: data => {
                hideModal(MODAL);
                toast('success', 'License created');
                const id = (data as { id?: string }).id;
                void List.Reload().then(() => { if (id) List.ShowDetail(id); });
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The container has to be the element that CONTAINS the form's
                // div.validation-summary -- the empty alert div next to it holds
                // none, so every model-level ('' key) message the Api sent was
                // dropped on the floor. Field-keyed messages find their own spans.
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create license', summaryOf(data));
                return true;
            },
            onComplete: () => working(false),
        });
    },

    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `License/Domain/${currentDomainId()}`,
            alertTitle: 'Failed to reload licenses',
        });
    },
};

export const Detail = {
    EnterEditMode(): void {
        const modal = one('.license-modal');
        if (!modal) return;
        modal.classList.add('editing');
        const name = modal.querySelector('.lc-name') as HTMLInputElement | null;
        name?.focus();
        name?.select();
    },

    CancelEdit(): void {
        Detail.Reload(licenseId());
    },

    SaveHeader(): void {
        const id = licenseId();
        const modal = one('.license-modal');
        if (!id || !modal) return;
        const value = (selector: string): string =>
            (modal.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? '';
        busy(true);
        void ajaxJson({
            url: absoluteUrl(`License/${id}`),
            method: 'PUT',
            data: {
                Id: id,
                Name: value('.lc-name'),
                Description: value('.lc-description'),
                ValidStart: value('.lc-valid-start') || null,
                ValidEnd: value('.lc-valid-end') || null,
                Status: 0,   // the server preserves status; this PUT only touches name/description/window
            },
            alertInto: DETAIL_ALERT,
            errorTitle: 'Failed to save license',
            onSuccess: () => {
                toast('success', 'License saved');
                Detail.Reload(id);
            },
            onFailure: failToast('Failed to save license'),
            onComplete: () => busy(false),
        });
    },

    Delete(): void {
        const id = licenseId();
        if (!id) return;
        void confirm({
            title: 'Delete License',
            html: 'Delete this license? Person + group assignments and binder rows '
                + 'are removed too.<br/><br/>'
                + '<span class="text-danger"><strong>Auditable revoke</strong> is preferred '
                + 'over delete for license history.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                busy(true);
                void ajaxJson({
                    url: absoluteUrl(`License/${id}`),
                    method: 'DELETE',
                    alertInto: DETAIL_ALERT,
                    errorTitle: 'Failed to delete license',
                    onSuccess: () => {
                        hideModal(MODAL);
                        toast('success', 'License deleted');
                        void List.Reload();
                    },
                    onFailure: failToast('Failed to delete license'),
                    onComplete: () => busy(false),
                });
            },
        });
    },

    // ----- status transitions -----
    Suspend(): void {
        void transition('Suspend', 'Suspend License', 'Suspend this license? Reversible &mdash; re-Activate to restore.', 'warning');
    },

    Activate(): void {
        void transition('Activate', 'Activate License', 'Re-activate this license?', 'primary');
    },

    Revoke(): void {
        void transition('Revoke', 'Revoke License',
            'Revoke this license?<br/><br/><span class="text-danger">This is <strong>one-way</strong>. '
            + 'To restore coverage you must issue a new license.</span>', 'danger');
    },

    // ----- Properties tab -----
    AddProperty(): void {
        const id = licenseId();
        const pane = one('#LicenseTabProperties');
        if (!id || !pane) return;
        const property = readProperty(pane, 'lc-add-prop');
        if (!property) return;
        void ajaxJson({
            url: absoluteUrl(`License/${id}/Property`),
            method: 'POST',
            data: property,
            errorTitle: 'Failed to add property',
            onSuccess: () => {
                toast('success', 'Property added');
                void reloadTab('#LicenseTabProperties');
            },
            onFailure: failToast('Failed to add property'),
        });
    },

    DeleteProperty(propertyId: number): void {
        void confirm({
            title: 'Remove Property',
            html: 'Remove this property from the license?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(`License/Property/${propertyId}`),
                    method: 'DELETE',
                    errorTitle: 'Failed to delete property',
                    onSuccess: () => {
                        toast('success', 'Property removed');
                        void reloadTab('#LicenseTabProperties');
                    },
                    onFailure: failToast('Failed to delete property'),
                });
            },
        });
    },

    // ----- Persons + Groups + Tenants tab -----
    AssignPerson(): void {
        assign('Person', 'lc-add-person-id', 'Pick a person first', 'Person assigned', 'Failed to assign person');
    },

    UnassignPerson(personId: number): void {
        unassign('Person', personId, 'Remove this person assignment?<br/><br/>'
            + 'Group and tenant-blanket coverage paths are unaffected.',
            'Person unassigned', 'Failed to unassign person');
    },

    AssignGroup(): void {
        assign('Group', 'lc-add-group-id', 'Pick a group first', 'Group assigned', 'Failed to assign group');
    },

    UnassignGroup(groupId: number): void {
        unassign('Group', groupId, 'Remove this group assignment?<br/><br/>'
            + 'Members keep coverage from any other path, and group membership itself is unaffected.',
            'Group unassigned', 'Failed to unassign group');
    },

    AssignTenant(): void {
        assign('Tenant', 'lc-add-tenant-id', 'Pick a tenant first', 'Tenant assigned', 'Failed to assign tenant');
    },

    UnassignTenant(tenantId: number): void {
        unassign('Tenant', tenantId, 'Remove this tenant assignment?<br/><br/>'
            + 'Members keep coverage from any other path, and tenant membership itself is unaffected.',
            'Tenant unassigned', 'Failed to unassign tenant');
    },

    // ----- Entities tab -----
    BindResource(): void {
        const id = licenseId();
        const pane = one('#LicenseTabEntities');
        if (!id || !pane) return;
        const uri = ((pane.querySelector('.lc-add-resource-uri') as HTMLInputElement | null)?.value ?? '').trim();
        if (!uri) {
            toast('danger', 'Enter the resource URI first');
            return;
        }
        const descendants = (pane.querySelector('.lc-add-resource-descendants') as HTMLInputElement | null)?.checked ?? false;
        void ajaxJson({
            url: absoluteUrl(`License/${id}/Resource`),
            method: 'POST',
            data: { uri, descendants: String(descendants) },
            errorTitle: 'Failed to bind resource',
            onSuccess: () => {
                toast('success', 'Resource bound');
                void reloadTab('#LicenseTabEntities');
            },
            onFailure: failToast('Failed to bind resource'),
        });
    },

    /**
     * Flip one row's Descendants flag by re-binding it. The tab reloads either way, so a
     * refused change puts the checkbox back where the server has it.
     */
    SetDescendants(resourceId: number, descendants: boolean): void {
        const id = licenseId();
        if (!id) return;
        void ajaxJson({
            url: absoluteUrl(`License/${id}/Resource/${resourceId}?descendants=${descendants}`),
            method: 'PUT',
            errorTitle: 'Failed to change descendants',
            onSuccess: () => toast('success', descendants ? 'Now covers descendants' : 'No longer covers descendants'),
            onFailure: failToast('Failed to change descendants'),
            onComplete: () => void reloadTab('#LicenseTabEntities'),
        });
    },

    ReleaseResource(resourceId: number): void {
        release(`License/${licenseId()}/Resource/${resourceId}`, 'Release Resource',
            'Release this resource binding?<br/><br/>Entities beneath it keep any coverage from their class or another binding.',
            'Resource released', 'Failed to release resource');
    },

    BindGroup(): void {
        const id = licenseId();
        const pane = one('#LicenseTabEntities');
        if (!id || !pane) return;
        const value = (selector: string): string =>
            ((pane.querySelector(selector) as HTMLInputElement | null)?.value ?? '').trim();
        const applicationId = value('.lc-add-class-application');
        const classPrefix = value('.lc-add-class-prefix');
        if (!applicationId || !classPrefix) {
            toast('danger', 'Pick an application and name the class');
            return;
        }
        void ajaxJson({
            url: absoluteUrl(`License/${id}/ResourceGroup`),
            method: 'POST',
            data: { applicationId, classPrefix, scopeUri: value('.lc-add-class-scope') },
            errorTitle: 'Failed to bind class',
            onSuccess: () => {
                toast('success', 'Class bound');
                void reloadTab('#LicenseTabEntities');
            },
            onFailure: failToast('Failed to bind class'),
        });
    },

    ReleaseGroup(groupId: number): void {
        release(`License/${licenseId()}/ResourceGroup/${groupId}`, 'Release Class',
            'Release this class binding?<br/><br/>Its members keep any coverage from another binding.',
            'Class released', 'Failed to release class');
    },

    // ----- Test Access -----
    RunTestAccess(): void {
        const pane = one('#LicenseTabAssignments');
        if (!pane) return;
        const id = (selector: string): number => Number((pane.querySelector(selector) as HTMLInputElement | null)?.value ?? '');
        const applicationId = id('.lc-test-application');
        const personId = id('.lc-test-person');
        const resourceId = id('.lc-test-resource');
        const operation = (pane.querySelector('.lc-test-operation') as HTMLSelectElement | null)?.value ?? '';
        if (!applicationId || !personId || !resourceId || !operation) {
            toast('danger', 'Application, Person, Resource, and Operation are all required');
            return;
        }
        const query = new URLSearchParams({
            applicationId: String(applicationId),
            personId: String(personId),
            resourceId: String(resourceId),
            operation,
        });
        void ajaxSwap({
            container: 'LicenseTestAccessResult',
            url: absoluteUrl(`License/TestAccess?${query}`),
            alertTitle: 'Test Access failed',
        });
    },

    Reload(id: string): void {
        void ajaxModal({ modal: MODAL, url: `License/Detail/${id}`, title: null });
    },

    /**
     * Download the signed export envelope.
     *
     * Two answers share the 200: the envelope, as a JSON attachment, and a
     * JsonModelStateResult saying why there isn't one (no signing certificate
     * configured is the usual reason). Content-Disposition is what tells them
     * apart, so this reads the response rather than going through ajaxJson.
     */
    Export(): void {
        const id = licenseId();
        if (!id) return;
        busy(true);
        void (async () => {
            try {
                const response = await fetch(absoluteUrl(`License/Export/${id}`), {
                    credentials: 'same-origin',
                    headers: { Accept: 'application/json' },
                });
                const contentType = response.headers.get('Content-Type') ?? '';
                const disposition = response.headers.get('Content-Disposition') ?? '';
                if (response.ok && contentType.startsWith('application/json') && disposition.includes('attachment')) {
                    const blob = await response.blob();
                    const href = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = href;
                    link.download = `license-${id}.signed.json`;
                    document.body.appendChild(link);
                    link.click();
                    URL.revokeObjectURL(href);
                    link.remove();
                    toast('success', 'License exported');
                    return;
                }
                const data = await response.json() as { modelState?: unknown; message?: string };
                const reason = data?.modelState ? modelStateSummary(data.modelState) : (data?.message || 'Export failed.');
                toast('danger', 'Failed to export license', reason);
            } catch (error) {
                toast('danger', 'Failed to export license', error instanceof Error ? error.message : '');
            } finally {
                busy(false);
            }
        })();
    },
};

// ---------------------------------------------------------------- helpers

/**
 * A status change, behind the console's own confirm dialog.
 *
 * The legacy version used window.confirm, which is the browser's chrome rather
 * than the console's and cannot say which way is one-way -- Revoke wore the
 * same plain grey box as Activate. Each transition now states its own
 * consequence in its own colour.
 */
function transition(action: string, title: string, html: string, variant: string): void {
    const id = licenseId();
    if (!id) return;
    void confirm({
        title,
        html,
        confirmText: action,
        variant,
        onConfirm: () => {
            const verb = action.toLowerCase();
            busy(true);
            void ajaxJson({
                url: absoluteUrl(`License/${id}/${action}`),
                method: 'POST',
                alertInto: DETAIL_ALERT,
                errorTitle: `Failed to ${verb} license`,
                onSuccess: () => {
                    toast('success', `License ${verb}d`);
                    Detail.Reload(id);
                },
                onFailure: failToast(`Failed to ${verb} license`),
                onComplete: () => busy(false),
            });
        },
    });
}

/** The ClaimType / ClaimValue / ClaimValueType triple an add-row carries, or null when incomplete. */
export function readProperty(root: ParentNode, prefix: string):
    { ClaimType: string; ClaimValue: string; ClaimValueType: string } | null {
    const value = (suffix: string): string =>
        ((root.querySelector(`.${prefix}-${suffix}`) as HTMLInputElement | null)?.value ?? '').trim();
    const claimType = value('type');
    const claimValueType = value('vtype');
    if (!claimType || !claimValueType) {
        toast('danger', 'ClaimType and ValueType are required');
        return null;
    }
    return { ClaimType: claimType, ClaimValue: value('value'), ClaimValueType: claimValueType };
}

/** Person / Group / Tenant all assign the same way: one id from a picker, one POST, reload the tab. */
function assign(axis: string, inputClass: string, missing: string, done: string, failed: string): void {
    const id = licenseId();
    const pane = one('#LicenseTabAssignments');
    if (!id || !pane) return;
    const targetId = Number((pane.querySelector(`.${inputClass}`) as HTMLInputElement | null)?.value ?? '');
    if (!targetId || targetId <= 0) {
        toast('danger', missing);
        return;
    }
    void ajaxJson({
        url: absoluteUrl(`License/${id}/${axis}/${targetId}`),
        method: 'POST',
        errorTitle: failed,
        onSuccess: () => {
            toast('success', done);
            void reloadTab('#LicenseTabAssignments');
        },
        onFailure: failToast(failed),
    });
}

/** Release one Entities-tab binding behind a confirm, then reload that tab. */
function release(path: string, title: string, html: string, done: string, failed: string): void {
    void confirm({
        title,
        html,
        confirmText: 'Release',
        variant: 'danger',
        onConfirm: () => {
            void ajaxJson({
                url: absoluteUrl(path),
                method: 'DELETE',
                errorTitle: failed,
                onSuccess: () => {
                    toast('success', done);
                    void reloadTab('#LicenseTabEntities');
                },
                onFailure: failToast(failed),
            });
        },
    });
}

function unassign(axis: string, targetId: number, html: string, done: string, failed: string): void {
    const id = licenseId();
    if (!id) return;
    void confirm({
        title: `Remove ${axis} Assignment`,
        html,
        confirmText: 'Remove',
        variant: 'danger',
        onConfirm: () => {
            void ajaxJson({
                url: absoluteUrl(`License/${id}/${axis}/${targetId}`),
                method: 'DELETE',
                errorTitle: failed,
                onSuccess: () => {
                    toast('success', done);
                    void reloadTab('#LicenseTabAssignments');
                },
                onFailure: failToast(failed),
            });
        },
    });
}
